import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserCheck, UserMinus, UserPlus, UserRoundCog } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { PlatformMemberRow } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pill } from '@/features/finance/pill';
import { api, errorMessage } from '@/lib/api';
import { AddUserModal, useOrgRoles } from './add-user-modal';
import { ImpersonateModal } from './impersonate-modal';
import { Card, dd, IconAction, usePlatformAbility } from './shared';

export function useOrgMembers(orgId: string) {
  return useQuery({ queryKey: ['platform', 'org', orgId, 'members'], queryFn: () => api.get<PlatformMemberRow[]>(`/platform/orgs/${orgId}/members`) });
}

export const roleLabel = (m: Pick<PlatformMemberRow, 'roleKey' | 'roleName'>) => (m.roleKey === 'owner' ? 'Administrador' : m.roleName);

/** Membros da organização: papel, situação, adicionar, alterar papel, ativar/desativar, remover e entrar como. */
export function OrgMembersCard({ orgId, orgName }: { orgId: string; orgName: string }) {
  const qc = useQueryClient();
  const { canModerate } = usePlatformAbility();
  const members = useOrgMembers(orgId);
  const roles = useOrgRoles(orgId);
  const [adding, setAdding] = useState(false);
  const [impersonate, setImpersonate] = useState<PlatformMemberRow | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['platform'] });
  const patch = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: { roleKey?: string; active?: boolean } }) => api.patch(`/platform/orgs/${orgId}/members/${userId}`, body),
    onSuccess: () => {
      toast.success('Membro atualizado');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.del(`/platform/orgs/${orgId}/members/${userId}`),
    onSuccess: () => {
      toast.success('Membro removido da organização');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const roleSelect = (m: PlatformMemberRow) => (
    <Select value={m.roleKey ?? ''} onValueChange={(v) => v !== m.roleKey && patch.mutate({ userId: m.userId, body: { roleKey: v } })} disabled={!canModerate || patch.isPending}>
      <SelectTrigger size="sm" className="w-full min-w-52 max-w-64" aria-label={`Papel de ${m.name}`}>
        <SelectValue placeholder={m.roleName} />
      </SelectTrigger>
      <SelectContent>
        {(roles.data ?? []).map((r) => (
          <SelectItem key={r.key} value={r.key}>
            {r.key === 'owner' ? 'Administrador (dono)' : r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const actions = (m: PlatformMemberRow) =>
    canModerate && (
      <div className="flex shrink-0 items-center justify-end gap-1">
        {m.active && <IconAction label={`Entrar como ${m.name}`} icon={<UserRoundCog className="size-4" aria-hidden />} onClick={() => setImpersonate(m)} />}
        <IconAction
          label={`${m.active ? 'Desativar' : 'Ativar'} ${m.name}`}
          icon={m.active ? <UserMinus className="size-4" aria-hidden /> : <UserCheck className="size-4" aria-hidden />}
          disabled={patch.isPending}
          onClick={() => patch.mutate({ userId: m.userId, body: { active: !m.active } })}
        />
        <ConfirmDialog
          trigger={<IconAction label={`Remover ${m.name}`} icon={<Trash2 className="size-4" aria-hidden />} />}
          title={`Remover ${m.name}?`}
          description={`${m.name} perde o acesso a ${orgName}. A conta continua existindo e pode ser vinculada de novo. A organização precisa manter ao menos um administrador ativo.`}
          confirmLabel="Remover da organização"
          onConfirm={() => remove.mutateAsync(m.userId).catch(() => undefined)}
        />
      </div>
    );

  const status = (m: PlatformMemberRow) => (
    <span className="flex flex-wrap items-center gap-1.5">
      <Pill tone={m.active ? 'good' : 'neutral'}>{m.active ? 'Ativo' : 'Inativo'}</Pill>
      {m.mustChangePassword && <Pill tone="warn">Troca de senha pendente</Pill>}
    </span>
  );

  return (
    <Card
      title="Membros"
      description="Quem acessa esta organização. Ela sempre mantém ao menos um administrador ativo."
      actions={
        canModerate && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <UserPlus className="size-4" aria-hidden /> Adicionar usuário
          </Button>
        )
      }
    >
      {members.isLoading ? (
        <LoadingState />
      ) : members.error || !members.data ? (
        <ErrorState error={members.error} onRetry={members.refetch} />
      ) : members.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum membro.</p>
      ) : (
        <>
          <div className="hidden lg:block">
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="w-[26%] py-1 font-medium">Nome</th>
                  <th className="w-64 py-1 font-medium">Papel</th>
                  <th className="py-1 font-medium">Situação</th>
                  <th className="w-24 py-1 font-medium">Desde</th>
                  <th className="w-28 py-1 text-right font-medium">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.data.map((m) => (
                  <tr key={m.userId} className="border-t align-middle">
                    <td className="py-2 pr-3">
                      <span className="block truncate font-medium" title={m.name}>{m.name}</span>
                      <span className="block truncate text-xs text-muted-foreground" title={m.email}>{m.email}</span>
                    </td>
                    <td className="py-2 pr-3">
                      {canModerate ? roleSelect(m) : <Pill tone={m.roleKey === 'owner' ? 'warn' : 'neutral'}>{roleLabel(m)}</Pill>}
                    </td>
                    <td className="py-2 pr-3">{status(m)}</td>
                    <td className="tabular py-2 pr-3 whitespace-nowrap">{dd(m.since)}</td>
                    <td className="py-2 text-right">
                      {actions(m)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="grid gap-3 lg:hidden">
            {members.data.map((m) => (
              <li key={m.userId} className="grid gap-2 rounded-md border p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="block truncate font-medium" title={m.name}>{m.name}</span>
                    <span className="block truncate text-xs text-muted-foreground" title={m.email}>{m.email}</span>
                  </div>
                  {actions(m)}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {!canModerate && <Pill tone={m.roleKey === 'owner' ? 'warn' : 'neutral'}>{roleLabel(m)}</Pill>}
                  {status(m)}
                </div>
                {canModerate && roleSelect(m)}
                <span className="text-xs text-muted-foreground">Desde {dd(m.since)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <AddUserModal open={adding} onOpenChange={setAdding} org={{ id: orgId, name: orgName }} />
      {impersonate && <ImpersonateModal key={impersonate.userId} orgId={orgId} orgName={orgName} initialUserId={impersonate.userId} onClose={() => setImpersonate(null)} />}
    </Card>
  );
}
